import { useEffect, useRef, useState } from 'react';
import { CaretDown, PencilSimple, Plus, Tag, Trash } from 'shared/icons';
import presetManager from '../../services/PresetManager';

/**
 * PresetSelector
 *
 * Componente dropdown para selecao de presets.
 * Exibe presets do sistema e customizados, com acoes de editar/excluir.
 */
const PresetSelector = ({ onCreatePreset, onEditPreset }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activePreset, setActivePreset] = useState(null);
    const [systemPresets, setSystemPresets] = useState([]);
    const [customPresets, setCustomPresets] = useState([]);
    const dropdownRef = useRef(null);

    // Carregar presets ao montar
    useEffect(() => {
        loadPresets();

        // Listener para mudancas
        const handleChange = () => {
            loadPresets();
        };

        presetManager.addListener(handleChange);

        return () => {
            presetManager.removeListener(handleChange);
        };
    }, []);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const loadPresets = () => {
        setActivePreset(presetManager.getActivePreset());
        setSystemPresets(presetManager.getSystemPresets());
        setCustomPresets(presetManager.getCustomPresets());
    };

    const handleSelectPreset = (presetId) => {
        try {
            presetManager.setActivePreset(presetId);
            setIsOpen(false);
        } catch (error) {
            console.error('Erro ao selecionar preset:', error);
        }
    };

    const handleDeletePreset = async (e, presetId) => {
        e.stopPropagation();

        if (!window.confirm('Tem certeza que deseja excluir este preset?')) {
            return;
        }

        try {
            await presetManager.deletePreset(presetId);
        } catch (error) {
            console.error('Erro ao excluir preset:', error);
            alert(`Erro ao excluir preset: ${error.message}`);
        }
    };

    const handleEditPreset = (e, preset) => {
        e.stopPropagation();
        setIsOpen(false);
        onEditPreset(preset);
    };

    const handleCreatePreset = () => {
        setIsOpen(false);
        onCreatePreset();
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Botao do seletor */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-black dark:text-white bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-lg hover:bg-mono-50 dark:hover:bg-mono-900 transition-colors"
            >
                <Tag size={18} weight="bold" />
                <span>{activePreset?.name || 'Selecionar Preset'}</span>
                <CaretDown size={16} weight="bold" className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-lg shadow-lg z-50 overflow-hidden">
                    {/* Botao Criar Preset */}
                    <button
                        onClick={handleCreatePreset}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors border-b border-mono-200 dark:border-mono-800"
                    >
                        <Plus size={18} weight="bold" />
                        <span>Criar Preset</span>
                    </button>

                    {/* Lista de presets */}
                    <div className="max-h-96 overflow-y-auto">
                        {/* Presets do Sistema */}
                        {systemPresets.length > 0 && (
                            <div>
                                <div className="px-4 py-2 text-xs font-semibold text-mono-500 dark:text-mono-500 uppercase tracking-wider bg-mono-50 dark:bg-mono-900">
                                    Presets do Sistema
                                </div>
                                {systemPresets.map((preset) => (
                                    <button
                                        key={preset.id}
                                        onClick={() => handleSelectPreset(preset.id)}
                                        className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-mono-50 dark:hover:bg-mono-900 transition-colors ${
                                            activePreset?.id === preset.id
                                                ? 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
                                                : 'text-black dark:text-white'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            {activePreset?.id === preset.id && (
                                                <span className="text-blue-600 dark:text-blue-400" />
                                            )}
                                            <span className="font-medium">{preset.name}</span>
                                        </div>
                                        <span className="px-2 py-0.5 text-xs font-medium text-mono-600 dark:text-mono-400 bg-mono-100 dark:bg-mono-800 rounded">
                                            Sistema
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Presets Customizados */}
                        {customPresets.length > 0 && (
                            <div>
                                <div className="px-4 py-2 text-xs font-semibold text-mono-500 dark:text-mono-500 uppercase tracking-wider bg-mono-50 dark:bg-mono-900 border-t border-mono-200 dark:border-mono-800">
                                    Meus Presets
                                </div>
                                {customPresets.map((preset) => (
                                    <div
                                        key={preset.id}
                                        className={`group relative flex items-center justify-between px-4 py-3 text-sm hover:bg-mono-50 dark:hover:bg-mono-900 transition-colors ${
                                            activePreset?.id === preset.id ? 'bg-blue-50 dark:bg-blue-950' : ''
                                        }`}
                                    >
                                        <button
                                            onClick={() => handleSelectPreset(preset.id)}
                                            className={`flex-1 flex items-center gap-2 text-left ${
                                                activePreset?.id === preset.id
                                                    ? 'text-blue-600 dark:text-blue-400'
                                                    : 'text-black dark:text-white'
                                            }`}
                                        >
                                            {activePreset?.id === preset.id && (
                                                <span className="text-blue-600 dark:text-blue-400" />
                                            )}
                                            <span className="font-medium">{preset.name}</span>
                                        </button>

                                        {/* Acoes (visiveis no hover) */}
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => handleEditPreset(e, preset)}
                                                className="p-1.5 text-mono-600 dark:text-mono-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950 rounded transition-colors"
                                                title="Editar preset"
                                            >
                                                <PencilSimple size={16} weight="bold" />
                                            </button>
                                            <button
                                                onClick={(e) => handleDeletePreset(e, preset.id)}
                                                className="p-1.5 text-mono-600 dark:text-mono-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors"
                                                title="Excluir preset"
                                            >
                                                <Trash size={16} weight="bold" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Empty state para presets customizados */}
                        {customPresets.length === 0 && (
                            <div className="px-4 py-6 text-center text-sm text-mono-500 dark:text-mono-500 border-t border-mono-200 dark:border-mono-800">
                                Nenhum preset customizado ainda.
                                <br />
                                Clique em "Criar Preset" para comecar.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PresetSelector;
